/**
 * Générateur — Attendu 192 — O2 « Il met en ordre et/ou complète des blocs
 * fournis par le professeur pour construire un programme simple sur un logiciel
 * de programmation. »
 * Niveau : 4ème | Thème #12 Algorithmique (O2) | Session A.
 * Source prof : O2 (Créé n°19 « le carré de 100 pixels », l'exemple de réussite
 * mot pour mot) + D2S2.Ex.7 « la frise programmée » (DOUBLE ÉTIQUETTE O6, portée
 * ici) + PI-5ᵉ S3.Ex.1a « factoriser le carré » (le geste de compression, déjà
 * calé en 5ᵉ).
 *
 * ADAPTATIONS scoring : « assemble les blocs » → ordonner d'une chaîne LINÉAIRE
 * stricte (la boucle est présentée comme UN bloc pré-assemblé → ordre unique,
 * ré-dérivable par rang de mot-clé ; l'ambiguïté avancer/tourner du doc est
 * traitée à part en carte « intervertir »). « réécris avec la boucle » → drag.
 *
 * 5 cartes :
 *  1. carre_ordre      — assembler le programme du carré (ordonner).
 *  2. carre_interieur  — quels blocs à l'intérieur de la boucle ? (QCM multiple).
 *  3. carre_intervertir — avancer ⇄ tourner : que trace-t-on ? (QCM).
 *  4. frise_espacement — de combien les motifs sont-ils espacés ? (saisie).
 *  5. factoriser       — réécrire le carré avec la boucle (drag).
 */

import {
    randInt, randomChoice, shuffle, makeQcm, makeSaisie, makeOrdonner, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';
import { svgScript, svgScripts, imgElement } from './algo';

const ATTENDU_ID = 192;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CARRE_ORDRE = 'Le carré : assembler le programme';
const L_CARRE_INTERIEUR = "Le carré : à l'intérieur de la boucle";
const L_CARRE_INTERVERTIR = 'Le carré : intervertir avancer et tourner';
const L_FRISE = 'La frise programmée : l\'espacement';
const L_FACTORISER = 'Factoriser le carré avec une boucle';

// 25 valeurs de côté → variété suffisante (empreinte portée par le TEXTE)
const COTES = Array.from({ length: 25 }, (_, i) => 30 + 5 * i);

// programme canonique du carré (côté c), boucle en C
const scriptCarre = (c) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'pen', txt: "stylo en position d'écriture" },
    { t: 'repeat', n: 4, body: [
        { t: 'move', txt: `avancer de ${c} pas` },
        { t: 'move', txt: 'tourner ↻ de 90 degrés' },
    ] },
    { t: 'pen', txt: 'relever le stylo' },
]);

// ═════════ Carte 1 — assembler le programme (ordonner) ═════════

const genCarreOrdre = () => {
    const c = randomChoice(COTES);
    // chaîne LINÉAIRE stricte : chapeau → poser le stylo → boucle → relever.
    const items = [
        'quand le drapeau est cliqué',
        "stylo en position d'écriture",
        `répéter 4 fois [ avancer de ${c} pas, tourner ↻ de 90 degrés ]`,
        'relever le stylo',
    ];
    const texte = '<p><b>On dispose des blocs suivants, en vrac. Remets-les dans l\'ordre '
        + `pour que le lutin trace un carré de côté ${c} pixels.</b></p>`
        + '<p>Le stylo doit écrire pendant le tracé, puis être relevé à la fin.</p>';
    const payload = makeOrdonner(items, texte, opts(L_CARRE_ORDRE));
    payload._meta.params = { c };
    return payload;
};

// ═════════ Carte 2 — à l'intérieur de la boucle (QCM multiple) ═════════

const genCarreInterieur = () => {
    const c = randomChoice(COTES);
    const correct = [`avancer de ${c} pas`, 'tourner ↻ de 90 degrés'];
    const choix = [
        ...correct,
        'relever le stylo',
        "stylo en position d'écriture",
        'quand le drapeau est cliqué',
    ];
    const texte = `<p><b>Voici le programme qui trace le carré de côté ${c} pixels. `
        + 'Quels blocs doivent être À L\'INTÉRIEUR de la boucle « répéter 4 fois » ?</b></p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_CARRE_INTERIEUR), elements_additionnels: imgElement(scriptCarre(c)) }, true);
    payload._meta.params = { c };
    return payload;
};

// ═════════ Carte 3 — intervertir avancer et tourner (QCM) ═════════

const genCarreIntervertir = () => {
    const c = randomChoice(COTES);
    const correct = 'Le même carré (seule son orientation de départ change).';
    const choix = [
        correct,
        'Un triangle.',
        'Une simple ligne droite.',
        'Rien du tout : le lutin ne bouge pas.',
    ];
    const texte = `<p><b>Dans la boucle du programme du carré de côté ${c} pixels, on intervertit `
        + 'les blocs « avancer » et « tourner ». Que trace alors le lutin ?</b></p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_CARRE_INTERVERTIR), elements_additionnels: imgElement(scriptCarre(c)) });
    payload._meta.params = { c };
    return payload;
};

// ═════════ Carte 4 — la frise programmée : l'espacement (saisie) ═════════

const genFriseEspacement = () => {
    const cote = randomChoice([30, 40, 50]);
    const espacement = cote + randomChoice([15, 20, 25]);   // pas > côté (frise propre)
    const nMotifs = randInt(4, 6);
    const deuxScripts = svgScripts([
        { titre: 'définir Motif', blocs: [
            { t: 'pen', txt: "stylo en position d'écriture" },
            { t: 'repeat', n: 4, body: [
                { t: 'move', txt: `avancer de ${cote} pas` },
                { t: 'move', txt: 'tourner ↻ de 90 degrés' },
            ] },
            { t: 'pen', txt: 'relever le stylo' },
        ] },
        { titre: 'Programme principal', blocs: [
            { t: 'hat', txt: 'quand le drapeau est cliqué' },
            { t: 'pen', txt: 'effacer tout' },
            { t: 'move', txt: 'aller à x : -210 y : 0' },
            { t: 'repeat', n: nMotifs, body: [
                { t: 'looks', txt: 'Motif' },
                { t: 'variable', txt: `ajouter ${espacement} à x` },
            ] },
        ] },
    ]);
    const texte = '<p><b>Léna programme une frise. Elle définit un bloc « Motif » (un carré), '
        + 'puis le répète en le décalant (programmes ci-dessous).</b></p>'
        + '<p><b>De combien de pas les motifs successifs sont-ils espacés ?</b></p>';
    const payload = makeSaisie(String(espacement), texte,
        { ...opts(L_FRISE), elements_additionnels: imgElement(deuxScripts) });
    payload._meta.params = { cote, espacement, nMotifs };
    return payload;
};

// ═════════ Carte 5 — factoriser le carré (drag) ═════════

const genFactoriser = () => {
    // côté ≠ 90 : la phrase drag contient déjà un trou {{90}} (angle) — un
    // côté de 90 pas dupliquerait la tuile
    const c = randomChoice(COTES.filter((v) => v !== 90));
    const phrase = `répéter {{4}} fois [ avancer de {{${c}}} pas, tourner ↻ de {{90}} degrés ]`;
    const distracteurs = filtrerDistracteurs(phrase,
        [String(c + 10), String(c - 10), '3', '2', '45', '180'].filter((v) => +v > 0))
        .slice(0, 3);
    const scriptLong = svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'move', txt: `avancer de ${c} pas` },
        { t: 'move', txt: 'tourner ↻ de 90 degrés' },
        { t: 'move', txt: `avancer de ${c} pas` },
        { t: 'move', txt: 'tourner ↻ de 90 degrés' },
        { t: 'move', txt: `avancer de ${c} pas` },
        { t: 'move', txt: 'tourner ↻ de 90 degrés' },
        { t: 'move', txt: `avancer de ${c} pas` },
        { t: 'move', txt: 'tourner ↻ de 90 degrés' },
    ]);
    const texte = `<p><b>Le programme du carré de côté ${c} écrit 4 fois de suite les mêmes `
        + 'blocs. Réécris-le avec moins d\'instructions grâce à la boucle '
        + '« répéter … fois ». Complète :</b></p>';
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_FACTORISER), mode: 'drag', distracteurs, elements_additionnels: imgElement(scriptLong) });
    payload._meta.params = { c };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    carre_ordre_ordonner: genCarreOrdre,
    carre_interieur_qcm_multiple: genCarreInterieur,
    carre_intervertir_qcm_unique: genCarreIntervertir,
    frise_espacement_texte_saisie: genFriseEspacement,
    factoriser_texte_trou_drag: genFactoriser,
};

const CARTES = [
    { id: 'carre_ordre', label: L_CARRE_ORDRE },
    { id: 'carre_interieur', label: L_CARRE_INTERIEUR },
    { id: 'carre_intervertir', label: L_CARRE_INTERVERTIR },
    { id: 'frise_espacement', label: L_FRISE },
    { id: 'factoriser', label: L_FACTORISER },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
